// src/components/pages/admin-dash/AdminMatchResultFilterBar.jsx
import React from "react";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import { PARAMETER_OPTIONS } from "../../../constants/parameterLabels";

/**
 * Shared, controlled filter bar for the 4 MatchResult admin reports
 * (by-parameter, top-prizes, hit-distribution, extra-ball-impact) —
 * all 4 backend endpoints under /admin/match-result take the same
 * optional typeLottery/parameter/startDate/endDate query params, so
 * the bar itself is the one piece of real UI sharing across the 4
 * report screens; everything below it (table/chart/tiles) is
 * per-report.
 *
 * Pure presentational — no fetch, no internal state. `extraSlot` lets
 * a specific report (e.g. Top Prizes' `limit` selector) inject an
 * extra field into the same row without a special case here.
 */
const LOTTERY_OPTIONS = [
  { value: "",               label: "All lotteries" },
  { value: "MEGA_MILLIONS",  label: "Mega Millions" },
  { value: "POWER_BALL",     label: "Powerball"     },
];

export default function AdminMatchResultFilterBar({
  typeLottery, setTypeLottery,
  parameter, setParameter,
  startDate, setStartDate,
  endDate, setEndDate,
  onSearch,
  onReset,
  loading = false,
  extraSlot,
}) {
  const hasFilter = typeLottery || parameter || startDate || endDate;

  return (
    <Card variant="soft" className="!p-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SelectField label="Lottery" value={typeLottery} onChange={setTypeLottery} options={LOTTERY_OPTIONS} />
        <SelectField label="Parameter" value={parameter} onChange={setParameter} options={PARAMETER_OPTIONS} />
        <Field label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="!mb-0" />
        <Field label="To" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="!mb-0" />
      </div>

      <div className="flex flex-wrap items-end justify-between gap-3 mt-3">
        <div>{extraSlot}</div>
        <div className="flex items-center gap-2">
          {hasFilter && (
            <button
              type="button"
              onClick={onReset}
              className="text-[12px] hover:underline px-1"
              style={{ color: "var(--txt-2)" }}
            >
              Reset
            </button>
          )}
          <Button icon="search" size="sm" onClick={onSearch} loading={loading}>Search</Button>
        </div>
      </div>
    </Card>
  );
}

export function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-[12px] font-medium mb-1.5 tracking-wide" style={{ color: "var(--txt)" }}>
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full px-3.5 py-[11px] text-[14px] outline-none rounded-full border appearance-none cursor-pointer pr-9"
          style={{
            background: "var(--surf-2)",
            color: "var(--txt)",
            borderColor: "var(--border-2)",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        <span
          className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[10px]"
          style={{ color: "var(--txt-3)" }}
        >
          ▾
        </span>
      </div>
    </div>
  );
}
